import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { ApiService, AuthorizationService, UserService } from '@tekman/onmat/service/generic';
import { StorageService } from '@tekman/shared/service';
import { appRoutes } from '@tekman/onmat/constants';
import { ProfileFacade } from '@tekman/onmat/store/profile';

@Injectable({
  providedIn: 'root',
})
export class LicensesGuard implements CanActivate {
  private languages: string[] = [];
  private cachedResult: UrlTree | boolean | null = null;

  constructor(
    private readonly apiService: ApiService,
    private readonly userService: UserService,
    private readonly storage: StorageService,
    private readonly profileFacade: ProfileFacade,
    private readonly router: Router,
    private readonly authorizationService: AuthorizationService
  ) {}

  async canActivate(): Promise<UrlTree | boolean> {
    if (this.cachedResult !== null) {
      return this.cachedResult;
    }

    const user = this.userService.getUser();
    const licenses = user?.attributes['license'];
    const hasLicenses = await this.loadLicenses(licenses);

    if (!hasLicenses) {
      // No profile is loaded here: the error page picks its own language from the
      // user's school and nothing else in the app is reachable without licenses.
      this.cachedResult = this.router.createUrlTree([appRoutes.errorLicense]);
      return this.cachedResult;
    }

    this.profileFacade.loadProfile(user);
    this.authorizationService.init();
    this.cachedResult = true;
    return true;
  }

  async loadLicenses(licenses: any): Promise<boolean> {
    const filterLicenses = Object.keys(licenses).flatMap(key => {
      return licenses[key].filter(item => {
        return item.programId === 'ONMAT' || item.programId === 'EMAT';
      });
    });
    const academicYearId = Object.keys(licenses).sort().pop();
    this.storage.selectedAcademicYearId = academicYearId;
    filterLicenses
      ?.filter(license => license.schoolYearId === academicYearId)
      .forEach(item => {
        item.levels
          .filter(level => level.stage === 'Secundaria')
          .forEach(level => {
            if (!this.languages.includes(level.language)) {
              this.languages.push(level.language);
            }
          });
      });

    this.userService.licenses = filterLicenses;
    if (!this.languages.length) {
      return false;
    }

    this.apiService.setLanguages(this.languages);
    await this.apiService.setApi(this.languages, academicYearId);
    return true;
  }
}
